// Import Actions
import Immutable from "seamless-immutable";
import {
  keyGraphProductDataList,
  keyGraphCollectionDataList,
  getSleepGoalCollectionKeyFromHandle,
} from "../utils/shop";
import * as Shop from "../types/Shop";
import APP from "../types/App";
import {
  addLineItemToOrderedLineItems,
  removeLineItemFromOrderedLineItems,
  setInitialOrderedLineItems,
  getProductKeyFromHandle,
} from "../utils/shop";

// Initial State
const INITIAL_STATE = Immutable({
  cartOpen: false,
  checkout: { lineItems: [] },
  fetching: false,
  updating: false,
  quantityUpdates: [],
  addingToCart: null,
  orderedLineItems: [],
  allProducts: {},
  giftWithPurchaseProducts: {},
  errors: {},
  collections: {
    hatchCollection: {
      products: {},
      metadata: {},
      isFetching: false,
    },
    sleepCollection: {
      products: {},
      metadata: {},
      subCollections: {},
      isFetching: false,
    },
    adults: {
      products: {},
      metadata: {},
      subCollections: {},
      isFetching: false,
    },
    babiesKids: {
      products: {},
      metadata: {},
      subCollections: {},
      isFetching: false,
    },
    accessories: {
      products: {},
      metadata: {},
      subCollections: {},
      isFetching: false,
    },
  },
});

const ShopReducer = (state = INITIAL_STATE, action) => {
  const immutableState = Immutable.isImmutable(state)
    ? state
    : Immutable(state);

  const handle = getProductKeyFromHandle(action.handle);
  switch (action.type) {
    case Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE:
      let currentGWPProductCount =
        immutableState.giftWithPurchaseProducts[action.productKey];

      return immutableState.merge(
        {
          checkout: action.checkout,
          giftWithPurchaseProducts: {
            ...immutableState.giftWithPurchaseProducts,
            [action.productKey]:
              currentGWPProductCount > 0 ? currentGWPProductCount - 1 : 0,
          },
        },
        { deep: true }
      );
    case Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE:
      let gwpProductCount = state.giftWithPurchaseProducts[action.productKey];

      return immutableState.merge({
        checkout: action.checkout,
        giftWithPurchaseProducts: {
          ...immutableState.giftWithPurchaseProducts,
          [action.productKey]: gwpProductCount ? gwpProductCount++ : 1,
        },
      });
    case Shop.SHOW_CART:
      return immutableState.merge({
        cartOpen: true,
      });
    case Shop.HIDE_CART:
      return immutableState.merge({
        cartOpen: false,
      });
    case Shop.UPDATE_LINE_ITEMS_REQUEST:
    case Shop.UPDATE_CHECKOUT_REQUEST:
      return Immutable.updateIn(immutableState, ["updating"], () => true);
    case Shop.REMOVE_DISCOUNT_CODE_REQUEST:
    case Shop.APPLY_DISCOUNT_CODE_REQUEST:
    case Shop.CREATE_CHECKOUT_REQUEST:
      return Immutable.updateIn(immutableState, ["fetching"], () => true);
    case Shop.FETCH_COLLECTION_REQUEST:
      return Immutable.updateIn(
        immutableState,
        ["collections", handle, "isFetching"],
        () => true
      );
    case Shop.INVALID_DISCOUNT_CODE_APPLIED:
      return immutableState.merge({
        errors: { discountCode: action.errors },
        fetching: false,
      });
    case Shop.APPLY_DISCOUNT_CODE_FAILURE:
      return Immutable.updateIn(immutableState, ["fetching"], () => false);
    case Shop.APPLY_DISCOUNT_CODE_RECEIVE:
      const { discountCode: omit, ...rest } = immutableState.errors;

      return immutableState.merge({
        checkout: action.checkout,
        fetching: false,
        errors: rest,
      });
    case Shop.UPDATE_LINE_ITEMS_RECEIVE:
    case Shop.REMOVE_DISCOUNT_CODE_RECEIVE:
    case Shop.UPDATE_CHECKOUT_RECEIVE:
      return immutableState.merge({
        checkout: action.checkout,
        fetching: false,
        updating: false,
      });
    case Shop.CREATE_CHECKOUT_RECEIVE:
      return immutableState.merge({
        checkout: action.checkout,
        fetching: false,
        updating: false,
        orderedLineItems: [],
      });
    case Shop.FETCH_SLEEP_GOAL_SUB_COLLECTIONS_FAILURE:
      return Immutable.updateIn(
        immutableState,
        ["collections", "sleepCollection", "isFetching"],
        () => false
      );
    case Shop.FETCH_SLEEP_GOAL_SUB_COLLECTIONS_REQUEST:
      return Immutable.updateIn(
        immutableState,
        ["collections", "sleepCollection", "isFetching"],
        () => true
      );
    case Shop.FETCH_SLEEP_GOAL_SUB_COLLECTIONS_RECEIVE:
      // @tedlin182 - Need to format the keys to strip 'sleep-goal-'
      return immutableState.merge(
        {
          collections: {
            sleepCollection: {
              isFetching: false,
              subCollections: keyGraphCollectionDataList(
                action.collections,
                getSleepGoalCollectionKeyFromHandle
              ),
            },
          },
        },
        { deep: true }
      );
    case Shop.FETCH_COLLECTION_RECEIVE:
      const { products, title, image, description } = action.collection;
      const keyedProductList = keyGraphProductDataList(products);

      return immutableState.merge(
        {
          allProducts: { ...immutableState.allProducts, ...keyedProductList },
          collections: {
            [handle]: {
              products: keyedProductList,
              metadata: { title, image, description },
              isFetching: false,
            },
          },
        },
        { deep: true }
      );
    case Shop.UPDATE_QUANTITY_REQUEST:
      return Immutable.updateIn(immutableState, ["fetching"], () => true);
    case Shop.UPDATE_QUANTITY_RECEIVE:
      return immutableState.merge({
        checkout: action.checkout,
        fetching: false,
      });
    case Shop.ADD_TO_CART_REQUEST:
      return immutableState.merge({
        fetching: true,
        addingToCart: action.variantId,
      });
    case Shop.ADD_TO_CART_RECEIVE:
      return immutableState.merge({
        checkout: action.checkout,
        fetching: false,
        addingToCart: null,
        orderedLineItems: addLineItemToOrderedLineItems(
          immutableState.orderedLineItems,
          immutableState.addingToCart
        ),
      });
    case Shop.REMOVE_FROM_CART_REQUEST:
      return Immutable.updateIn(immutableState, ["fetching"], () => true);
    case Shop.REMOVE_FROM_CART_RECEIVE:
      return immutableState.merge({
        checkout: action.checkout,
        fetching: false,
        orderedLineItems: removeLineItemFromOrderedLineItems(
          immutableState.orderedLineItems,
          action.variantId
        ),
      });
    case Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_REQUEST:
      return Immutable.updateIn(immutableState, ["quantityUpdates"], () =>
        immutableState.quantityUpdates.concat([action.lineItem.id])
      );
    case Shop.START_QUANTITY_UPDATE:
      return Immutable.updateIn(immutableState, ["quantityUpdates"], () =>
        immutableState.quantityUpdates.concat([action.lineItemId])
      );
    case Shop.COMPLETE_QUANTITY_UPDATE:
      return Immutable.updateIn(immutableState, ["quantityUpdates"], () =>
        immutableState.quantityUpdates.filter(
          (item) => item !== action.lineItemId
        )
      );
    case Shop.STALE_CHECKOUT_ID:
      return immutableState.merge({
        orderedLineItems: INITIAL_STATE.orderedLineItems,
      });
    case Shop.UPDATE_LINE_ITEMS_FAILURE:
    case Shop.UPDATE_CHECKOUT_REQUEST_FAILURE:
    case Shop.CREATE_CHECKOUT_REQUEST_FAILURE:
    case Shop.ADD_TO_CART_REQUEST_FAILURE:
    case Shop.REMOVE_FROM_CART_REQUEST_FAILURE:
    case Shop.UPDATE_QUANTITY_REQUEST_FAILURE:
      return immutableState.merge({
        fetching: false,
        updating: false,
        addingToCart: null,
      });
    case Shop.FETCH_COLLECTION_REQUEST_FAILURE:
      return Immutable.updateIn(
        immutableState,
        ["collections", handle, "isFetching"],
        () => false
      );
    case APP.REHYDRATION_COMPLETE:
      // After initial push, if users have items in their cart,
      // we need to set orderedLineItems with the current line items
      // order
      return INITIAL_STATE.merge([
        immutableState,
        {
          // For people who already have items in their cart,
          // we'll need to set this new 'orderedLineItems' property using the
          // existing checkout.lineItems list.
          orderedLineItems: setInitialOrderedLineItems(
            immutableState.checkout.lineItems,
            immutableState.orderedLineItems
          ),
          errors: {},
        },
      ]);
    default:
      return immutableState;
  }
};

// Export Reducer
export default ShopReducer;
